import {useSignal} from '@preact/signals-react'
import {useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {Sidebar} from 'primereact/sidebar'
import {forwardRef, useImperativeHandle, useMemo} from 'react'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../query-manager/QueryKeysManager'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {checkEmptyString} from '../../utils/CommonUtils'
import {useAuth} from '../authentication/AuthProvider'
import CustomForm from '../form-utils/CustomForm'
import {formLayout, getComposedDataList} from '../form/Constant'
import FormHeader from '../form/FormHeader'
import PageContentWrapper from '../layout/PageContentWrapper'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {clientList} from './ClientConstants'
import {useClientCRUD} from './query-keys/ClientQueryManager'

const ClientForm = forwardRef((props, ref) => {
    useSignals()
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    //#region Context
    const {user} = useAuth()
    //#endregion

    //#region Translate
    const {translateCommons} = TranslateUtils()
    //#endregion

    //#region State
    const data = useSignal({})
    const visible = useSignal(false)
    //#endregion

    //#region Query
    const queryClient = useQueryClient()

    const {mutateAsync: createOrUpdateClient} = useClientCRUD(
        !!data.value?.id ? QueryCRUDTypes.update : QueryCRUDTypes.create,
        user?.id,
    )
    //#endregion

    //#region Memo
    const dataList = useMemo(() => {
        return getComposedDataList(clientList, data.value)
    }, [data.value])
    //#endregion

    //#region Handle Function
    const handleShowForm = value => {
        blockOverflownScroll()
        data.value = value && Object.keys(value)?.length > 0 ? {...value} : {}
        visible.value = true
    }

    const handleHideForm = () => {
        unblockOverflownScroll()
        data.value = {}
        visible.value = false
    }

    const handleOnSubmit = async value => {
        if (
            !value?.organizationName ||
            !!checkEmptyString(value.organizationName)
        ) {
            showNotification(
                severityNotification.error,
                'Organization Name is required !!',
            )
            return
        }

        if (!value?.firstName || !!checkEmptyString(value.firstName)) {
            showNotification(
                severityNotification.error,
                'First Name is required !!',
            )
            return
        }

        if (
            !value?.languageConfiguration ||
            value?.languageConfiguration?.length === 0
        ) {
            showNotification(
                severityNotification.warn,
                'Main languages for client is required !!',
            )
            return
        }

        let toPostData = {
            ...value,
            id: data.value?.id || null,
            userId: data.value?.userId || null,
            name: value?.organizationName,
            languages: value?.languageConfiguration,
        }

        const clientData = await createOrUpdateClient(toPostData)

        if (clientData?.id) {
            const {clientKeys} = QueryKeyManager(QueryKeyTypes.client, user?.id)
            queryClient.invalidateQueries({
                queryKey: clientKeys.pattern(),
            })

            handleHideForm()
        }
    }
    //#endregion

    return (
        <Sidebar
            className="p-sidebar-full "
            visible={visible.value}
            onHide={handleHideForm}
            fullScreen>
            <PageContentWrapper>
                <FormHeader
                    header={translateCommons('CLIENT FORM')}
                    onHide={handleHideForm}
                />
                <CustomForm
                    dataList={dataList}
                    layout={formLayout.group}
                    onSubmit={handleOnSubmit}
                />
            </PageContentWrapper>
        </Sidebar>
    )
})

export default ClientForm
